<template>
  <div class="user-management">
    <h2>用户管理</h2>
    
    <!-- 添加用户表单 -->
    <div class="add-user-form">
      <h3>添加新用户</h3>
      <form @submit.prevent="addUser">
        <div class="form-group">
          <label for="username">用户名:</label>
          <input type="text" id="username" v-model="newUser.username" required>
        </div>
        
        <div class="form-group">
          <label for="email">邮箱:</label>
          <input type="email" id="email" v-model="newUser.email" required>
        </div>
        
        <div class="form-group">
          <label for="password">密码:</label>
          <input type="password" id="password" v-model="newUser.password" required>
        </div>
        
        <div class="form-group">
          <label for="role">角色:</label>
          <select id="role" v-model="newUser.role" required>
            <option value="">请选择角色</option>
            <option v-for="role in availableRoles" :key="role.value" :value="role.displayName">
              {{ role.displayName }}
            </option>
          </select>
        </div>
        
        <button type="submit" :disabled="addingUser">
          {{ addingUser ? '添加中...' : '添加用户' }}
        </button>
      </form>
    </div>
    
    <!-- 用户列表 -->
    <div class="user-list">
      <h3>用户列表</h3>
      <div v-if="loading">加载中...</div>
      <div v-else-if="users.length === 0">暂无用户</div>
      <div v-else>
        <table class="user-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>用户名</th>
              <th>邮箱</th>
              <th>角色</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.id">
              <td>{{ user.id }}</td>
              <td>{{ user.username }}</td>
              <td>{{ user.email }}</td>
              <td>{{ user.role }}</td>
              <td>{{ user.createdAt || 'N/A' }}</td>
              <td>
                <div class="action-buttons" v-if="user.username !== 'admin'">
                  <button 
                    class="btn-edit" 
                    @click="showEditUserModal(user)"
                    :disabled="editingUser"
                  >
                    编辑
                  </button>
                  <button 
                    class="btn-change-password" 
                    @click="showChangePasswordModal(user)"
                    :disabled="changingPassword"
                  >
                    修改密码
                  </button>
                  <button 
                    class="btn-delete" 
                    @click="confirmDeleteUser(user)"
                    :disabled="deletingUser"
                  >
                    删除
                  </button>
                </div>
                <span v-else class="super-admin-label">平台管理员</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 编辑用户模态框 -->
    <div v-if="showEditModal" class="modal-overlay" @click="closeEditModal">
      <div class="modal-content" @click.stop>
        <h3>编辑用户信息</h3>
        <p>用户: {{ selectedUser.username }}</p>
        <form @submit.prevent="updateUser">
          <div class="form-group">
            <label for="editUsername">用户名:</label>
            <input 
              type="text" 
              id="editUsername" 
              v-model="editForm.username" 
              required
            >
          </div>
          <div class="form-group">
            <label for="editEmail">邮箱:</label>
            <input 
              type="email" 
              id="editEmail" 
              v-model="editForm.email" 
              required
            >
          </div>
          <div class="form-group">
            <label for="editRole">角色:</label>
            <select id="editRole" v-model="editForm.role" required>
              <option value="">请选择角色</option>
              <option v-for="role in availableRoles" :key="role.value" :value="role.displayName">
                {{ role.displayName }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label for="editPassword">新密码 (留空则不修改):</label>
            <input 
              type="password" 
              id="editPassword" 
              v-model="editForm.password"
              minlength="6"
            >
          </div>
          <div class="modal-actions">
            <button type="button" @click="closeEditModal" :disabled="editingUser">
              取消
            </button>
            <button type="submit" :disabled="editingUser">
              {{ editingUser ? '保存中...' : '确认保存' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 修改密码模态框 -->
    <div v-if="showPasswordModal" class="modal-overlay" @click="closePasswordModal">
      <div class="modal-content" @click.stop>
        <h3>修改用户密码</h3>
        <p>用户: {{ selectedUser.username }}</p>
        <form @submit.prevent="changeUserPassword">
          <div class="form-group">
            <label for="newPassword">新密码:</label>
            <input 
              type="password" 
              id="newPassword" 
              v-model="passwordForm.newPassword" 
              required
              minlength="6"
            >
          </div>
          <div class="form-group">
            <label for="confirmPassword">确认密码:</label>
            <input 
              type="password" 
              id="confirmPassword" 
              v-model="passwordForm.confirmPassword" 
              required
              minlength="6"
            >
          </div>
          <div class="modal-actions">
            <button type="button" @click="closePasswordModal" :disabled="changingPassword">
              取消
            </button>
            <button type="submit" :disabled="changingPassword || !isPasswordValid">
              {{ changingPassword ? '修改中...' : '确认修改' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'UserManagementPage',
  data() {
    return {
      users: [],
      loading: true,
      addingUser: false,
      changingPassword: false,
      deletingUser: false,
      editingUser: false,
      showPasswordModal: false,
      showEditModal: false,
      selectedUser: null,
      availableRoles: [],
      newUser: {
        username: '',
        email: '',
        password: '',
        role: ''
      },
      editForm: {
        username: '',
        email: '',
        password: '',
        role: ''
      },
      passwordForm: {
        newPassword: '',
        confirmPassword: ''
      }
    }
  },
  mounted() {
    // 检查用户角色
    const { authService } = require('@/services/authService');
    if (!authService.isAdmin()) {
      this.$router.push('/dashboard/profile');
      return;
    }
    
    this.loadUsers()
    this.loadRoles()
  },
  computed: {
    isPasswordValid() {
      return this.passwordForm.newPassword && 
             this.passwordForm.confirmPassword && 
             this.passwordForm.newPassword === this.passwordForm.confirmPassword &&
             this.passwordForm.newPassword.length >= 6
    }
  },
  methods: {
    loadUsers() {
      this.loading = true
      const user = JSON.parse(localStorage.getItem('user'))
      
      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      }
      
      if (user && user.token) {
        config.headers['Authorization'] = `Bearer ${user.token}`
      }
      
      api.get('/users', config)
        .then(response => {
          this.users = response.data
          this.loading = false
        })
        .catch(error => {
          console.error('获取用户列表失败:', error)
          this.loading = false
          
          // 如果是权限错误，跳转到统计数据页面
          if (error.response && error.response.status === 403) {
            this.$router.push('/dashboard/profile');
          }
        })
    },
    
    loadRoles() {
      const user = JSON.parse(localStorage.getItem('user'))
      
      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      }
      
      if (user && user.token) {
        config.headers['Authorization'] = `Bearer ${user.token}`
      }
      
      api.get('/users/roles', config)
        .then(response => {
          console.log('角色数据:', response.data)
          this.availableRoles = response.data
        })
        .catch(error => {
          console.error('获取角色列表失败:', error)
        })
    },
    
    addUser() {
      this.addingUser = true
      const user = JSON.parse(localStorage.getItem('user'))
      
      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      }
      
      if (user && user.token) {
        config.headers['Authorization'] = `Bearer ${user.token}`
      }
      
      api.post('/users', this.newUser, config)
        .then(() => {
          this.addingUser = false
          alert('用户添加成功')
          this.newUser = { username: '', email: '', password: '', role: '' }
          this.loadUsers()
        })
        .catch(error => {
          this.addingUser = false
          console.error('添加用户失败:', error)
          alert('添加用户失败')
        })
    },

    // 显示编辑用户模态框
    showEditUserModal(user) {
      this.selectedUser = user
      this.editForm = {
        username: user.username,
        email: user.email,
        password: '',
        role: user.role
      }
      this.showEditModal = true
    },

    // 关闭编辑用户模态框
    closeEditModal() {
      this.showEditModal = false
      this.selectedUser = null
      this.editForm = {
        username: '',
        email: '',
        password: '',
        role: ''
      }
    },

    // 更新用户信息
    updateUser() {
      this.editingUser = true
      const user = JSON.parse(localStorage.getItem('user'))
      
      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      }
      
      if (user && user.token) {
        config.headers['Authorization'] = `Bearer ${user.token}`
      }
      
      api.put(`/users/${this.selectedUser.id}`, this.editForm, config)
        .then(() => {
          this.editingUser = false
          alert('用户信息更新成功')
          this.closeEditModal()
          this.loadUsers()
        })
        .catch(error => {
          this.editingUser = false
          console.error('更新用户失败:', error)
          alert('更新用户失败: ' + (error.response?.data?.message || error.message))
        })
    },

    // 显示修改密码模态框
    showChangePasswordModal(user) {
      this.selectedUser = user
      this.passwordForm = {
        newPassword: '',
        confirmPassword: ''
      }
      this.showPasswordModal = true
    },

    // 关闭修改密码模态框
    closePasswordModal() {
      this.showPasswordModal = false
      this.selectedUser = null
      this.passwordForm = {
        newPassword: '',
        confirmPassword: ''
      }
    },

    // 修改用户密码
    changeUserPassword() {
      if (!this.isPasswordValid) {
        alert('密码不匹配或长度不足6位')
        return
      }

      this.changingPassword = true
      const user = JSON.parse(localStorage.getItem('user'))
      
      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      }
      
      if (user && user.token) {
        config.headers['Authorization'] = `Bearer ${user.token}`
      }

      const passwordData = {
        password: this.passwordForm.newPassword
      }
      
      api.put(`/users/${this.selectedUser.id}/password`, passwordData, config)
        .then(() => {
          this.changingPassword = false
          alert('密码修改成功')
          this.closePasswordModal()
        })
        .catch(error => {
          this.changingPassword = false
          console.error('修改密码失败:', error)
          alert('修改密码失败: ' + (error.response?.data?.message || error.message))
        })
    },

    // 确认删除用户
    confirmDeleteUser(user) {
      if (confirm(`确定要删除用户 "${user.username}" 吗？此操作不可撤销。`)) {
        this.deleteUser(user)
      }
    },

    // 删除用户
    deleteUser(user) {
      this.deletingUser = true
      const currentUser = JSON.parse(localStorage.getItem('user'))
      
      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      }
      
      if (currentUser && currentUser.token) {
        config.headers['Authorization'] = `Bearer ${currentUser.token}`
      }
      
      api.delete(`/users/${user.id}`, config)
        .then(() => {
          this.deletingUser = false
          alert('用户删除成功')
          this.loadUsers()
        })
        .catch(error => {
          this.deletingUser = false
          console.error('删除用户失败:', error)
          alert('删除用户失败: ' + (error.response?.data?.message || error.message))
        })
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 2rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.add-user-form {
  background: linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%);
  padding: 2rem;
  border-radius: 20px;
  margin-bottom: 2rem;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
}

.add-user-form h3 {
  margin: 0 0 1.5rem 0;
  color: #1e293b;
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #1e293b, #475569);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  box-sizing: border-box;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: rgba(255,255,255,0.8);
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  background: rgba(255,255,255,0.95);
}

button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

button:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

button:disabled {
  background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.user-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
}

.user-table th,
.user-table td {
  padding: 1.25rem 1.5rem;
  text-align: left;
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.user-table th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.user-table tr:hover {
  background: linear-gradient(90deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  transform: scale(1.01);
  transition: all 0.3s ease;
}

.user-table tr:last-child td {
  border-bottom: none;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.btn-edit {
  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(23, 162, 184, 0.3);
}

.btn-edit:hover {
  background: linear-gradient(135deg, #138496 0%, #0f6674 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(23, 162, 184, 0.4);
}

.btn-change-password {
  background: linear-gradient(135deg, #28a745 0%, #218838 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
}

.btn-change-password:hover {
  background: linear-gradient(135deg, #218838 0%, #1e7e34 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.4);
}

.btn-delete {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.btn-delete:hover {
  background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.4);
}

.admin-label {
  color: #6c757d;
  font-style: italic;
  font-size: 12px;
}

.super-admin-label {
  color: #dc3545;
  font-weight: bold;
  font-size: 12px;
  background-color: #f8d7da;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #f5c6cb;
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 400px;
  max-width: 500px;
}

.modal-content h3 {
  margin-top: 0;
  margin-bottom: 10px;
  color: #333;
}

.modal-content p {
  margin-bottom: 20px;
  color: #666;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.modal-actions button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.modal-actions button[type="button"] {
  background-color: #6c757d;
  color: white;
}

.modal-actions button[type="button"]:hover {
  background-color: #5a6268;
}

.modal-actions button[type="submit"] {
  background-color: #007bff;
  color: white;
}

.modal-actions button[type="submit"]:hover {
  background-color: #0056b3;
}

.modal-actions button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}
</style>